import React, {useState, useCallback} from 'react';
import {useDropzone} from 'react-dropzone';
import {Scan, Upload, Copy, Loader2, AlertTriangle, FileImage, Image} from 'lucide-react';

const OcrPage = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [ocrText, setOcrText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [preview, setPreview] = useState(null);

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setSelectedFile(file);
            setOcrText('');
            setError(null);

            // Create preview
            const reader = new FileReader();
            reader.onload = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }, []);

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tiff']
        },
        maxFiles: 1
    });

    const extractText = async () => {
        if (!selectedFile) {
            setError('Please select an image file first');
            return;
        }

        setIsLoading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/ocr-urdu/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`OCR failed with status: ${response.status}`);
            }

            const data = await response.json();
            setOcrText(data.text);
        } catch (error) {
            console.error('Error during OCR:', error);
            setError(error.message || 'An error occurred during text extraction');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyText = () => {
        navigator.clipboard.writeText(ocrText)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => {
                console.error('Failed to copy text: ', err);
            });
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Scan className="text-blue-600" size={32}/>
                    Urdu OCR
                </h1>
                <p className="mt-2 text-gray-600">
                    Extract Urdu text from printed books, documents, and images
                </p>
            </div>

            {/* Main content */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* File upload section */}
                <div className="space-y-6">
                    <h2 className="text-lg font-semibold text-gray-800">Input File</h2>
                    <div {...getRootProps()} className={`
            border-2 ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-dashed border-gray-300'} 
            rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors
          `}>
                        <input {...getInputProps()} />

                        <div className="flex flex-col items-center">
                            {isDragActive ? (
                                <>
                                    <Upload className="text-blue-500 mb-3" size={48}/>
                                    <p className="text-blue-600 font-medium">Drop the image file here</p>
                                </>
                            ) : (
                                <>
                                    <FileImage className="text-gray-400 mb-3" size={48}/>
                                    <p className="text-gray-600 font-medium">Drag & drop an image file here</p>
                                    <p className="text-gray-500 text-sm mt-2">or click to select a file</p>
                                </>
                            )}
                            <p className="text-xs text-gray-500 mt-4">
                                Supported formats: PNG, JPG, JPEG, GIF, BMP, TIFF
                            </p>
                        </div>
                    </div>

                    {preview && (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2 border-b">
                                <div className="flex items-center">
                                    <Image className="text-gray-500 mr-2" size={16}/>
                                    <p className="text-sm font-medium text-gray-700">{selectedFile.name}</p>
                                </div>
                            </div>
                            <div className="p-2 flex justify-center">
                                <img
                                    src={preview}
                                    alt="Preview"
                                    className="max-h-64 object-contain rounded"
                                />
                            </div>
                        </div>
                    )}

                    <button
                        onClick={extractText}
                        disabled={isLoading || !selectedFile}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors ${
                            isLoading || !selectedFile
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="animate-spin" size={18}/>
                                Extracting Text...
                            </>
                        ) : (
                            <>
                                <Scan size={18}/>
                                Extract Text
                            </>
                        )}
                    </button>
                </div>

                {/* OCR result section */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gray-800">Extracted Text</h2>

                        {ocrText && (
                            <button
                                onClick={handleCopyText}
                                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                                <Copy size={14}/>
                                {copied ? 'Copied!' : 'Copy Text'}
                            </button>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-center p-3 bg-red-50 text-red-700 rounded-md">
                            <AlertTriangle size={16} className="mr-2 flex-shrink-0"/>
                            <p className="text-sm">{error}</p>
                        </div>
                    )}

                    <div
                        className={`w-full h-80 p-4 border border-gray-300 rounded-lg overflow-auto ${
                            ocrText ? 'font-urdu' : 'text-gray-400'
                        }`}
                        style={{
                            fontFamily: ocrText ? 'Jameel Noori Nastaleeq, Calibri, sans-serif' : 'inherit',
                            fontSize: ocrText ? '1.1rem' : 'inherit',
                            direction: 'rtl'
                        }}
                    >
                        {ocrText}
                    </div>

                    {ocrText && (
                        <div className="flex items-center">
                            <div className="flex-1 h-px bg-gray-200"></div>
                            <p className="px-3 text-xs text-gray-500">Urdu OCR Result</p>
                            <div className="flex-1 h-px bg-gray-200"></div>
                        </div>
                    )}

                    {!ocrText && !isLoading && selectedFile && (
                        <div className="text-center p-4">
                            <p className="text-sm text-gray-600">
                                Click "Extract Text" to process your image
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OcrPage;